import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";

const EMIRATES = ["All", "Dubai", "Abu Dhabi", "Sharjah", "Ras Al Khaimah", "Ajman", "Fujairah", "Umm Al Quwain"];

export default function AdminDevelopers() {
  const [items, setItems] = useState([]);
  const [emirate, setEmirate] = useState("All");
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    api.get("/admin/developers").then((r) => setItems(r.data.items || []));
  }, []);
  useEffect(load, [load]);

  const filtered = useMemo(() => {
    return items.filter((d) =>
      (emirate === "All" || d.emirate === emirate || (d.emirates_of_operation || []).includes(emirate)) &&
      (!q || (d.name || "").toLowerCase().includes(q.toLowerCase()))
    );
  }, [items, emirate, q]);

  const remove = async (slug) => {
    if (!window.confirm(`Delete developer "${slug}"?`)) return;
    await api.delete(`/admin/developers/${slug}`);
    toast.success("Developer deleted");
    load();
  };

  const resolveLogo = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    return `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}${url}`;
  };

  return (
    <div className="p-10" data-testid="admin-developers">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <div className="text-xs tracking-[0.24em] uppercase text-gray-500 mb-1">Directory</div>
          <h1 className="font-serif text-4xl">Developers</h1>
          <p className="text-sm text-gray-600 mt-1">{items.length} developers · upload real logos and manage metadata.</p>
        </div>
        <Link
          to="/admin/developers/new"
          data-testid="admin-dev-new-btn"
          className="px-5 py-3 bg-[#001588] text-white text-sm uppercase tracking-[0.2em] hover:bg-black"
        >+ New developer</Link>
      </div>

      <div className="mb-6 bg-white border border-[#E5E7EB] p-4 flex flex-wrap items-center gap-2">
        {EMIRATES.map((e) => (
          <button
            key={e}
            data-testid={`admin-dev-emirate-${e.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setEmirate(e)}
            className={`px-3 py-1.5 text-xs uppercase tracking-[0.2em] border transition-colors ${
              emirate === e ? "bg-[#001588] text-white border-[#001588]" : "bg-white text-neutral-700 border-neutral-200 hover:border-[#001588]"
            }`}
          >{e}</button>
        ))}
        <input
          data-testid="admin-dev-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search developer…"
          className="ml-auto text-sm border border-[#E5E7EB] px-3 py-1.5 min-w-[220px] bg-white text-neutral-900 focus:outline-none focus:border-[#001588]"
        />
      </div>

      <div className="border border-[#E5E7EB] bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-[#E5E7EB] text-left uppercase text-xs tracking-wider text-gray-500">
            <tr>
              <th className="px-4 py-3 w-16">Logo</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Emirate</th>
              <th className="px-4 py-3">Est.</th>
              <th className="px-4 py-3">Website</th>
              <th className="px-4 py-3">Publish</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E7EB]">
            {filtered.map((d) => (
              <tr key={d.id || d.slug} data-testid={`admin-dev-row-${d.slug}`}>
                <td className="px-4 py-3">
                  {d.logo ? (
                    <img src={resolveLogo(d.logo)} alt={d.name} className="w-10 h-10 object-contain border border-[#E5E7EB] bg-white" />
                  ) : (
                    <div className="w-10 h-10 bg-gray-100 border border-[#E5E7EB]" />
                  )}
                </td>
                <td className="px-4 py-3 font-medium">{d.name}</td>
                <td className="px-4 py-3 text-gray-600">{d.emirate || "—"}</td>
                <td className="px-4 py-3 text-gray-600">{d.year_established || d.founded || "—"}</td>
                <td className="px-4 py-3 text-gray-600 text-xs truncate max-w-[220px]">{d.website ? d.website.replace(/^https?:\/\//, "") : "—"}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 border ${
                    (d.publish_status === "published" || d.published) ? "bg-emerald-50 border-emerald-200 text-emerald-800" :
                    "bg-gray-50 border-gray-200 text-gray-700"
                  }`}>
                    {d.publish_status || (d.published ? "published" : "draft")}
                  </span>
                </td>
                <td className="px-4 py-3 text-right space-x-3">
                  <Link to={`/admin/developers/${d.slug}/edit`} data-testid={`admin-dev-edit-${d.slug}`} className="text-[#001588] hover:underline text-xs uppercase tracking-wider">Edit</Link>
                  <button onClick={() => remove(d.slug)} data-testid={`admin-dev-delete-${d.slug}`} className="text-red-600 hover:underline text-xs uppercase tracking-wider">Delete</button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-500">No developers match this filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
